// API service for payment methods
import apiClient from './apiClient';

export interface StoredPaymentMethod {
  id: string;
  type: 'BANK_ACCOUNT' | 'BPAY' | 'APPLE_WALLET' | 'GOOGLE_WALLET' | 'CARD';
  label: string;
  userEmail: string;
  bankName?: string;
  accountHolderName?: string;
  accountNumberMasked?: string;
  bsbOrRouting?: string;
  bpayBillerCode?: string;
  bpayReference?: string;
  walletProvider?: string;
  verified: boolean;
  isDefault: boolean;
  createdAt: string;
}

export interface AddBankAccountRequest {
  label: string;
  token: string; // Account number (will be encrypted server-side)
  bsb?: string;
  accountName?: string;
  bankName?: string;
}

export interface AddBPayRequest {
  label: string;
  reference: string;
  billerCode?: string;
}

export interface AddWalletRequest {
  label: string;
  walletTokenId: string;
  provider: 'APPLE' | 'GOOGLE';
}

export interface PaymentMethodResponse {
  id: string;
  error?: string;
}

const paymentMethodsApi = {
  /**
   * Get all payment methods for the logged-in user
   */
  async list(): Promise<StoredPaymentMethod[]> {
    return apiClient.get<StoredPaymentMethod[]>('/payments/methods');
  },

  /**
   * Add a bank account payment method
   */
  async addBankAccount(data: AddBankAccountRequest): Promise<PaymentMethodResponse> {
    return apiClient.post<PaymentMethodResponse>('/payments/methods/bank', data);
  },

  /**
   * Add a BPAY payment method
   */
  async addBPay(data: AddBPayRequest): Promise<PaymentMethodResponse> {
    return apiClient.post<PaymentMethodResponse>('/payments/methods/bpay', data);
  },

  /**
   * Add Apple Wallet payment method
   */
  async addAppleWallet(data: AddWalletRequest): Promise<PaymentMethodResponse> {
    return apiClient.post<PaymentMethodResponse>('/payments/methods/wallet', {
      ...data,
      provider: 'APPLE'
    });
  },

  /**
   * Add Google Wallet payment method
   */
  async addGoogleWallet(data: AddWalletRequest): Promise<PaymentMethodResponse> {
    return apiClient.post<PaymentMethodResponse>('/payments/methods/wallet', {
      ...data,
      provider: 'GOOGLE'
    });
  },

  /**
   * Delete a payment method
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`/payments/methods/${id}`);
  },

  /**
   * Set a payment method as default
   */
  async setDefault(id: string): Promise<void> {
    return apiClient.put<void>(`/payments/methods/${id}/default`, {});
  }
};

export default paymentMethodsApi;

